import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/modal.css";

const ContractViewModal = ({ isOpen, onClose, onConfirm, userData = {} }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const contentRef = useRef(null);

  // 현재 날짜를 한국어 형식으로 가져오기
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const day = today.getDate();

  // 기본값 설정 (userData가 제대로 전달되지 않았을 경우를 대비)
  const {
    block = "000",
    unit = "0000",
    shareName = "홍길동",
    apartment = "테스트아파트",
    claimAssignmentName = "홍길동",
    claimAssignmentPhone = "000",
    claimAssignmentBirth = "000",
  } = userData;

  // 한국어 조사 처리 함수
  const getKoreanParticle = (name) => {
    if (!name) return "이";
    const lastName = name.charAt(name.length - 1);
    const hasBatchim = (char) => {
      const code = char.charCodeAt(0);
      return (code - 44032) % 28 !== 0;
    };
    return hasBatchim(lastName) ? "이" : "가";
  };

  useEffect(() => {
    // 모달이 열릴 때마다 스크롤 상태 초기화
    if (isOpen) {
      setHasScrolledToBottom(false);
      setHasStartedScrolling(false);

      // 모달이 열릴 때 스크롤을 맨 위로 이동
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }

      // 스크롤 가능한지 확인
      setTimeout(() => {
        const element = contentRef.current;
        if (element) {
          if (element.scrollHeight <= element.clientHeight) {
            // 스크롤이 필요 없는 경우 자동으로 스크롤 완료 처리
            setHasScrolledToBottom(true);
          }
        }
      }, 50);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const element = e.target;

    // 스크롤이 조금이라도 시작되었는지 확인
    if (element.scrollTop > 10 && !hasStartedScrolling) {
      setHasStartedScrolling(true);
    }

    // 스크롤이 바닥에 도달했는지 확인 (여유 20px 추가)
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  };

  // 스크롤을 아래로 자동으로 내리는 함수
  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
      setHasStartedScrolling(true);
    }
  };

  // 확인 버튼 클릭 핸들러
  const handleConfirm = () => {
    if (hasScrolledToBottom && agreementChecked) {
      onConfirm();
      onClose();
    } else if (!hasScrolledToBottom) {
      // 스크롤을 자동으로 내려줌
      scrollToBottom();
    }
  };

  // 애니메이션 설정
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.15,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.15,
        ease: "easeIn",
      },
    },
  };

  const contentVariants = {
    hidden: { y: "100%" },
    visible: {
      y: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
        mass: 0.8,
      },
    },
    exit: {
      y: "100%",
      transition: {
        duration: 0.2,
        ease: [0.32, 0, 0.67, 0], // 부드러운 이징 함수 적용
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="agreement-modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="agreement-modal-content"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* <div className="agreement-modal-header">
              <div className="close-button" onClick={onClose}>
                ×
              </div>
              <h1>위임장</h1>
            </div> */}

            <div
              className="agreement-modal-body"
              ref={contentRef}
              onScroll={handleScroll}
            >
              <div className="agreement-document">
                <h2 className="agreement-title">
                  공동소유자 채권양도계약 동의/승낙
                </h2>

                <div className="agreement-text">
                  <div
                    style={{
                      marginBottom: "30px",
                      lineHeight: "1.6",
                      padding: "20px",
                      backgroundColor: "#F8F9FA",
                      borderRadius: "12px",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "15px",
                      }}
                    >
                      하자보수 및 손해배상청구권 양도
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        marginBottom: "20px",
                      }}
                    >
                      1. "갑"은 하자보수의무자(시행사, 시공사, 보증사 등
                      사업주체)에 대하여 가지고 있는 하자보수청구 및 하자보수에
                      갈음하는 손해배상금(이자 및 지연손해금 포함)청구권을
                      "을"에게 양도하기로 하고 이에 본 양도증서에 서명날인한다.
                    </p>

                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "15px",
                      }}
                    >
                      채권 행사 및 소송 권한 위임
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        marginBottom: "20px",
                      }}
                    >
                      2. "갑"은 위 하자보수의무자들을 상대로 한 위
                      손해배상채권의 양도통지 및 손해배상채권행사 등 하자소송에
                      관한 일체의 권한(단, 준공전 하자를 포함한 합의종결권한은
                      제외함. 만일, 준공전 하자를 포함하여 합의종결을 할 경우,
                      "을"은 개별 구분소유자로부터 다시 동의를 받아야 하고 전체
                      85% 이상의 동의요건을 충족하여야만 합의종결이 가능함)을
                      "을"에게 위임한다.
                    </p>

                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "15px",
                      }}
                    >
                      승소금(합의금 포함) 사용 방법
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        marginBottom: "20px",
                      }}
                    >
                      3. 승소판결원리금(합의금 포함)의 사용 및 분배방법은 "을"의
                      의결에 따르되, ㉮ 계약에 의한 변호사 보수 및
                      소송대납비용은 위 승소금에서 우선공제・정산하고, ㉯
                      "㉮"항의 비용을 제한 나머지 금액 중 전유부분 판결금은 각
                      세대(구분소유자)에 지급하고, 공용부분 판결금(안전관리비,
                      소송비용액확정결정에 따른 회수금 포함)은 입주자대표회의의
                      의결에 따라 하자보수를 위한 공사비용으로 사용함에 "갑"은
                      동의한다.
                    </p>

                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "15px",
                      }}
                    >
                      동의 및 철회 불가 확인
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        marginBottom: "0px",
                      }}
                    >
                      4. "갑"은 본 동의(계약)가 정상적인 의사와 권한으로
                      성립(체결)되었음을 확인하고, 동의의사를 철회하지 않기로
                      하며, "을"이 "갑"을 위하여 하자보수청구 및 하자담보추급권
                      행사(하자소송 포함)등 하자관련 업무를 진행함에 있어
                      개인정보수집·이용·제공에 동의한다.
                    </p>
                  </div>

                  <div
                    style={{
                      marginBottom: "20px",
                      textAlign: "left",
                      fontSize: "16px",
                      color: "#666666",
                      fontWeight: "500",
                      padding: "0 10px",
                    }}
                  >
                    본인의 공동소유자 {claimAssignmentName}
                    {getKoreanParticle(claimAssignmentName)} 본인을 대리하여
                    체결한
                    <br />
                    채권양도계약의 내용을 확인하고 이에 동의합니다.
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      marginBottom: "20px",
                      width: "100%",
                    }}
                  >
                    <div
                      onClick={() => setAgreementChecked(!agreementChecked)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        padding: "15px",
                        borderRadius: "8px",
                        backgroundColor: agreementChecked
                          ? "#F9F8FF"
                          : "#ffffff",
                        border: agreementChecked
                          ? "1px solid #7A4FFF"
                          : "1px solid #E5E7EB",
                        width: "100%",
                        justifyContent: "flex-start",
                      }}
                    >
                      <div style={{ marginRight: "12px" }}>
                        <img
                          src={
                            agreementChecked
                              ? "/checked.png"
                              : "/checkedInActive.png"
                          }
                          alt="체크박스"
                          style={{ width: "20px", height: "20px" }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginBottom: "5px",
                          color: agreementChecked ? "#7A4FFF" : "#374151",
                        }}
                      >
                        동의합니다
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!hasScrolledToBottom && (
              <div className="scroll-indicator-bottom" onClick={scrollToBottom}>
                ↓ 내용을 끝까지 읽어주세요 ↓
              </div>
            )}

            <div className="agreement-modal-footer no-border">
              <button
                className="agreement-button"
                onClick={handleConfirm}
                disabled={!hasScrolledToBottom || !agreementChecked}
                style={{
                  backgroundColor:
                    hasScrolledToBottom && agreementChecked
                      ? "#7A4FFF"
                      : "#EBEBFF",
                  color:
                    hasScrolledToBottom && agreementChecked
                      ? "white"
                      : "#7A4FFF",
                  cursor:
                    hasScrolledToBottom && agreementChecked
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {hasScrolledToBottom && agreementChecked
                  ? "하단 내용을 확인했습니다"
                  : !hasScrolledToBottom
                  ? "내용을 모두 읽어주세요"
                  : "동의 체크박스를 선택해주세요"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContractViewModal;
